import { Avatar, Box, Grid, Hidden, SelectChangeEvent, Skeleton, SxProps, Theme, Typography } from "@mui/material";
import { CommonLayout } from "./commonComponents";
import BreadCrum from "../lib/BreadCrum";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "../redux-store/reducers";
import { useState, useEffect } from "react";
import { FamilyDetailsData, GetFamilyDetailsResponseBody, PortfolioList } from "../redux-store/types/investorProfile";
import { getFamilyDetails } from "../redux-store/actions/investorProfile";
import dayjs from "dayjs";
import { formatCurrency, isModuleAllowed, maskInfo } from "../utils/utilityFunctions";
import PieChartComponent from "./investorOverview/directEquity/directEquityOverview/PieChartComponent";
import FamilyPieChartComponent from "./investorOverview/common/FamilyPieChart";
import { storeInterfaceCode, storeInvestorClientId } from "../redux-store/actions/id";
import { storeSelectedPortfolio } from "../redux-store/actions/portfolioFilter";

export interface navigationData {
    clientCode?: string;
    familyName?: string;
    accountFilter?: number;
}


const FamilyInfo = ({
    columnWidth = { xs: 4 },
    personName,
    isAdmin,
    personDesignation,
    investmentValue,
    currentValue,
    overallReturns,
    irr,
    loading,
    handleNavigation,
    clientCode,
    portfolio,
    portfolioList,
    accountType
}: {
    columnWidth?: { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
    personName: string;
    isAdmin?: boolean;
    personDesignation: string | null;
    investmentValue: number;
    currentValue: number;
    overallReturns: number;
    irr: number;
    loading: boolean,
    handleNavigation?: (clientCode: string, investorName: string, portfolio: string, accountType: string) => void,
    clientCode?: string,
    portfolio: string;
    portfolioList: PortfolioList[];
    accountType: string
}): JSX.Element => {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {!loading && (
                <Grid
                    xs={columnWidth?.xs ?? false}
                    sm={columnWidth?.sm ?? false}
                    md={columnWidth?.md ?? false}
                    lg={columnWidth?.lg ?? false}
                    xl={columnWidth?.xl ?? false}
                    item
                    container
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                        borderRadius: "12px",
                        padding: "24px",
                        flexDirection: "column"
                    })}
                >
                    <Grid
                        item
                        container
                        sx={{
                            alignItems: "center",
                            flexWrap: "nowrap",
                            columnGap: "12px",
                            paddingBottom: "20px"
                        }}
                    >
                        <Grid item>
                            <Avatar
                                sx={(theme: Theme) => ({
                                    borderRadius: "12px",
                                    backgroundColor: theme.palette.grey[50],
                                    padding: { xs: "15px", sm: "18px", md: "20px", lg: "25px" },
                                    boxShadow: `0px 3.5px 5.5px 0px ${theme.palette.common.black}05`
                                })}
                            >
                                <Typography
                                    variant="body_600"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.primary[500],
                                        lineHeight: "22.5px"
                                    })}
                                >
                                    {personName.slice(0, 2).toUpperCase()}
                                </Typography>
                            </Avatar>
                        </Grid>
                        <Grid
                            item
                            container
                            xs={10}
                            sx={{
                                flexDirection: "column",
                                flexWrap: "nowrap",
                                rowGap: "3px"
                            }}
                        >
                            <Grid
                                xs
                                item
                                container
                                sx={{
                                    columnGap: "5px",
                                    flexWrap: "wrap",
                                    alignItems: "center"
                                }}
                            >
                                <Grid item sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                                    <Typography
                                        variant="body_600"
                                        onClick={() => handleNavigation ? handleNavigation(clientCode as string, personName, portfolio as string, accountType) : null}
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.primary[500],
                                            lineHeight: "22.5px",
                                            cursor: "pointer"
                                        })}
                                    >
                                        {personName}
                                    </Typography>
                                    {isAdmin && (
                                        <Grid
                                            item
                                            sx={(theme: Theme) => ({
                                                borderRadius: "8px",
                                                padding: "3px 10px",
                                                backgroundColor: theme.palette.grey[50]
                                            })}
                                        >
                                            <Typography
                                                variant="overline_400"
                                                sx={(theme: Theme) => ({
                                                    fontWeight: 500,
                                                    lineHeight: "15px",
                                                    letterSpacing: "0.02px",
                                                    color: theme.palette.primary[500]
                                                })}
                                            >
                                                Family Head
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid xs item>
                                <Typography
                                    variant="subtitle_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "17.5px",
                                        letterSpacing: "0.02px",
                                        color: theme.palette.text.secondary
                                    })}
                                >
                                    {personDesignation || "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    {portfolioList && portfolioList.length > 0 && (
                        <Grid
                            item
                            container
                            sx={(theme: Theme) => ({
                                flexDirection: "column",
                                marginTop: "16px",
                                borderTop: `1px solid ${theme.palette.customColors.lavender}`,
                                paddingTop: "12px"
                            })}
                        >
                            <Typography
                                variant="subtitle_600"
                                sx={(theme: Theme) => ({
                                    marginBottom: "8px",
                                    color: theme.palette.primary[500]
                                })}
                            >
                                Portfolio List
                            </Typography>

                            {portfolioList.map((p) => (
                                <Grid
                                    key={p.portfolio}
                                    container
                                    sx={(theme: Theme) => ({
                                        padding: "8px 0",
                                        borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`,
                                        alignItems: "center",
                                        cursor: "pointer",
                                        '&:hover': {
                                            backgroundColor: theme.palette.grey[50],
                                        }
                                    })}
                                    onClick={() =>
                                        handleNavigation
                                            ? handleNavigation(clientCode as string, personName, p.portfolio, p.cusAccountType)
                                            : null
                                    }
                                >
                                    <Grid item xs={4}>
                                        <Typography
                                            variant="overline_400"
                                            sx={(theme: Theme) => ({
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            {p.portfolioName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            variant="overline_400"
                                            sx={(theme: Theme) => ({
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            {p.cusAccountType}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography
                                            variant="overline_400"
                                            sx={(theme: Theme) => ({
                                                color: theme.palette.text.secondary
                                            })}
                                        >
                                            {p.modeOfHolding}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Typography
                                            variant="overline_400"
                                            sx={(theme: Theme) => ({
                                                textAlign: "right",
                                                color: theme.palette.primary[500]
                                            })}
                                        >
                                            View →
                                        </Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    <Typography
                        variant="subtitle_600"
                        sx={(theme: Theme) => ({
                            marginBottom: "8px",
                            color: theme.palette.primary[500],
                            mt: "16px"
                        })}
                    >
                        Investment Details
                    </Typography>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "6px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Investment Value
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: theme.palette.customColors.codGrey
                                })}
                            >
                                {formatCurrency({ value: investmentValue, locale, currencyCode })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Current Value
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: theme.palette.customColors.codGrey
                                })}
                            >
                                {formatCurrency({ value: currentValue, locale, currencyCode })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Overall Returns
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: overallReturns < 0 ? theme.palette.error[500] : theme.palette.success[500]
                                })}
                            >
                                {formatCurrency({ value: overallReturns, locale, currencyCode })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingTop: "12px",
                        }}
                    >
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "left",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.02px",
                                    color: theme.palette.customColors.greyFourty
                                })}
                            >
                                Absolute Return
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                variant="subtitle_500"
                                sx={(theme: Theme) => ({
                                    textAlign: "right",
                                    lineHeight: "17.5px",
                                    letterSpacing: "0.04px",
                                    color: irr < 0 ? theme.palette.error[500] : theme.palette.success[500]
                                })}
                            >
                                {irr?.toFixed(1)}%
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}
            {loading && (
                <Grid
                    {...{ columnWidth }}
                    item
                    container
                    sx={(theme: Theme) => ({
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                        borderRadius: "12px",
                        padding: "24px",
                        flexDirection: "column"
                    })}
                >
                    <Grid
                        item
                        container
                        sx={{
                            alignItems: "center",
                            flexWrap: "nowrap",
                            columnGap: "12px",
                            paddingBottom: "20px"
                        }}
                    >
                        <Grid item>
                            <Skeleton animation="wave" variant="circular" sx={{ width: "50px", height: "50px" }} />
                        </Grid>
                        <Grid
                            item
                            container
                            xs={10}
                            sx={{
                                flexDirection: "column",
                                flexWrap: "nowrap",
                                rowGap: "3px"
                            }}
                        >
                            <Grid
                                xs
                                item
                                container
                                sx={{
                                    columnGap: "5px",
                                    flexWrap: "wrap",
                                    alignItems: "center"
                                }}
                            >
                                <Grid item sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                                    <Skeleton animation="wave" width={'80px'} />
                                    {isAdmin && (
                                        <Grid
                                            item
                                            sx={(theme: Theme) => ({
                                                borderRadius: "8px",
                                                padding: "3px 10px",
                                                backgroundColor: theme.palette.grey[50]
                                            })}
                                        >
                                            <Skeleton animation="wave" width={'50px'} />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid xs item>
                                <Skeleton animation="wave" width={'50%'} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item container sx={{ flexDirection: 'column', marginTop: '16px' }}>
                        <Skeleton animation="wave" width="40%" height={24} sx={{ marginBottom: '8px' }} />
                        {[1, 2].map(i => (
                            <Grid key={i} container sx={{ padding: '8px 0' }}>
                                <Skeleton animation="wave" width="60%" />
                            </Grid>
                        ))}
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={(theme: Theme) => ({
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingBottom: "12px",
                            paddingTop: "12px",
                            borderBottom: `0.5px solid ${theme.palette.customColors.lavender}`
                        })}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        container
                        sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: 'nowrap',
                            paddingTop: "12px",
                        }}
                    >
                        <Grid item xs={6}>
                            <Skeleton animation="wave" width={'70%'} />
                        </Grid>
                        <Grid item xs={6}>
                            <Skeleton animation="wave" />
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </>
    )
}

const FamilyCards = ({ isLoading, familyData, apiError, isSuccess, handleClickEvent }: { isLoading: boolean, familyData: FamilyDetailsData, apiError: boolean, isSuccess: boolean, handleClickEvent: (clientCode: string, investorName: string, portfolio: string, accountType: string) => void }): JSX.Element => {
    return (
        <>
            {
                isLoading ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr",
                            },
                            gap: 2,
                        }}
                    >
                        {
                            new Array(2).fill(0).map((_, index) => (
                                <FamilyInfo
                                    key={index}
                                    columnWidth={{ xs: 6 }}
                                    personName="Dummy Name"
                                    isAdmin
                                    personDesignation="Dummy Designation"
                                    investmentValue={2222000}
                                    currentValue={2432000}
                                    overallReturns={122000}
                                    irr={25.56}
                                    loading={isLoading}
                                    portfolio="Dummy Portfolio"
                                    portfolioList={[]}
                                    accountType="Dummy Account Type"
                                />
                            ))
                        }
                    </Box >
                ) : (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: "1fr 1fr",
                            },
                            gap: 2,
                        }}
                    >
                        {familyData?.familyMembers?.length === 0 &&
                            <Grid
                                item
                                sx={(theme: Theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                    padding: '25px 30px 45px 30px',
                                    borderRadius: "20px",
                                    height: "fit-content"
                                })}
                            >
                                <Typography variant="h3" textAlign={"center"}>No data found</Typography>
                            </Grid>
                        }
                        {(apiError && !isSuccess) &&
                            <Grid
                                item
                                xs={12}
                                sx={(theme: Theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                    padding: '25px 30px 45px 30px',
                                    borderRadius: "20px",
                                    height: "fit-content"
                                })}
                            >
                                <Typography variant="h3" textAlign={"center"}>Something went wrong</Typography>
                            </Grid>
                        }
                        {!apiError && isSuccess && familyData?.familyMembers?.map((member, index) => (
                            <FamilyInfo
                                key={index}
                                columnWidth={{ xs: 12 }}
                                personName={member.name}
                                isAdmin={`${member?.isCustFamilyHead ?? ''}`.toLowerCase() === 'y' ? true : false}
                                personDesignation={member.relation}
                                investmentValue={member.investmentValue}
                                currentValue={member.currentValue}
                                overallReturns={member.overallReturns}
                                irr={member.irr}
                                loading={isLoading}
                                portfolio={member.portfolio}
                                portfolioList={member.portfolioList}
                                accountType={member.cusAccountType}
                                handleNavigation={handleClickEvent}
                            />
                        ))}
                    </Box>
                )
            }
        </>)
}

const FamilyChart = ({ isLoading, familyData, open, setOpen, apiError }: { isLoading: boolean, familyData: FamilyDetailsData, open: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>, apiError?: boolean }): JSX.Element => {
    return (
        <>
            {
                isLoading ? (
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                            padding: '25px 30px 45px 30px',
                            borderRadius: "20px",
                            height: "fit-content"
                        })}
                    >
                        <FamilyPieChartComponent
                            title="Family Distribution"
                            isLoading={isLoading}
                            pieChartData={familyData?.familyDistribution?.data}
                            pieChartLabelData={familyData?.familyDistribution?.labelData}
                            pieChartTotalValue={familyData?.familyDistribution?.totalValue}
                            open={open}
                            setOpen={setOpen}
                            apiError={apiError}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                            padding: '25px 30px 45px 30px',
                            borderRadius: "20px",
                            height: "fit-content"
                        })}
                    >

                        <FamilyPieChartComponent
                            title="Family Distribution"
                            isLoading={isLoading}
                            pieChartData={familyData?.familyDistribution?.data}
                            pieChartLabelData={familyData?.familyDistribution?.labelData}
                            pieChartTotalValue={familyData?.familyDistribution?.totalValue}
                            open={open}
                            setOpen={setOpen}
                            apiError={apiError}
                        />
                    </Box>
                )
            }
        </>
    )
}

const FamilyDetailsInfo = ({ isLoading, familyData, apiError, isSuccess }: { isLoading: boolean, familyData: FamilyDetailsData, apiError?: boolean, isSuccess?: boolean }): JSX.Element => {

    const { businessDate } = useSelector((state: RootStateType) => state.navbarInfo)
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <Grid container spacing={2}>
            {
                isLoading ? (
                    <>
                        <Grid
                            xs={6}
                            item
                            container
                            sx={{
                                columnGap: "15px",
                                alignItems: "flex-start",
                                flexWrap: "nowrap",
                                wordBreak: "break-word"
                            }}
                        >
                            <Grid item>
                                <Skeleton animation="wave" variant="circular" width={50} height={50} />
                            </Grid>
                            <Grid
                                item
                                container
                                sx={{
                                    flexDirection: "column",
                                    rowGap: "3px",
                                    flexWrap: "nowrap"
                                }}
                            >
                                <Grid item>
                                    <Skeleton animation="wave" />
                                </Grid>
                                <Grid item>
                                    <Skeleton animation="wave" width={'50%'} />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            xs={6}
                            item
                            container
                            sx={{
                                flexDirection: "column",
                                flexWrap: "nowrap",
                                rowGap: "3px",
                                alignItems: 'flex-end'
                            }}
                        >
                            <Grid item>
                                <Skeleton animation="wave" width={'120px'} />
                            </Grid>
                            <Grid item>
                                <Skeleton animation="wave" width={'140px'} />
                            </Grid>
                            <Grid item sx={{ marginTop: "8px" }}>
                                <Skeleton animation="wave" width={'170px'} height="30px" />
                            </Grid>
                            <Grid
                                item
                                container
                                sx={{
                                    flexWrap: "nowrap",
                                    marginTop: "30px",
                                    columnGap: "30px",
                                    justifyContent: "flex-end",
                                    alignItems: "center"
                                }}
                            >
                                <Grid
                                    xs="auto"
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "3px"
                                    }}
                                >
                                    <Grid item>
                                        <Skeleton animation="wave" />
                                    </Grid>
                                    <Grid item>
                                        <Skeleton animation="wave" width={'80px'} />
                                    </Grid>
                                </Grid>
                                <Grid
                                    xs="auto"
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "3px"
                                    }}
                                >
                                    <Grid item>
                                        <Skeleton animation="wave" />
                                    </Grid>
                                    <Grid item>
                                        <Skeleton animation="wave" width={'80px'} />
                                    </Grid>
                                </Grid>
                                <Grid
                                    xs="auto"
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "3px"
                                    }}
                                >
                                    <Grid item>
                                        <Skeleton animation="wave" />
                                    </Grid>
                                    <Grid item>
                                        <Skeleton animation="wave" width={'80px'} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid
                            xs={6}
                            item
                            // container
                            sx={{
                                columnGap: "15px",
                                alignItems: "flex-start",
                                display: "flex",
                                flexWrap: "nowrap",
                                wordBreak: "break-word"
                            }}
                        >
                            <Grid item>
                                <Avatar
                                    sx={(theme: Theme) => ({
                                        backgroundColor: theme.palette.primary[50],
                                        padding: { xs: "15px", sm: "18px", md: "20px", lg: "25px" }
                                    })}
                                >
                                    <Typography
                                        variant="h2"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.primary[500]
                                        })}
                                    >
                                        {familyData?.familyName?.[0]}
                                    </Typography>
                                </Avatar>
                            </Grid>
                            <Grid
                                item
                                container
                                sx={{
                                    flexDirection: "column",
                                    rowGap: "3px",
                                    flexWrap: "nowrap"
                                }}
                            >
                                {(apiError && !isSuccess) && <Typography variant="body_500">Something is went wrong</Typography>}
                                <Grid item>
                                    <Typography
                                        variant="h3"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "32.5px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {familyData?.familyName?.split(" ")[0]}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        variant="body_500"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            letterSpacing: "0.02px",
                                            color: theme.palette.text.secondary
                                        })}
                                    >
                                        {familyData?.familyName?.split(" ")[1] || ''}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            xs={6}
                            item
                            container
                            sx={{
                                flexDirection: "column",
                                flexWrap: "nowrap",
                                rowGap: "3px"
                            }}
                        >
                            <Grid item>
                                <Typography
                                    variant="body_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "24px",
                                        letterSpacing: "0.02px",
                                        textAlign: "right",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    Market Value
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography
                                    variant="subtitle_500"
                                    sx={(theme: Theme) => ({
                                        lineHeight: "21px",
                                        letterSpacing: "0.02px",
                                        textAlign: "right",
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {/* as on 30 Jan, 2024 */}
                                    as on {dayjs(businessDate, 'DD/MM/YYYY').format('DD MMM, YYYY')}
                                </Typography>
                            </Grid>
                            <Grid item sx={{ marginTop: "8px" }}>
                                <Typography
                                    variant="h1"
                                    sx={(theme: Theme) => ({
                                        fontSize: { xs: "24px", sm: "26px", md: "28px", lg: "30px", xl: "32px" },
                                        lineHeight: "40px",
                                        color: theme.palette.primary[500],
                                        textAlign: "right",
                                        whiteSpace: "nowrap"
                                    })}
                                >
                                    {formatCurrency({ value: familyData?.marketValue, locale, currencyCode })}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                container
                                sx={{
                                    flexWrap: "nowrap",
                                    marginTop: "30px",
                                    columnGap: "30px",
                                    justifyContent: "flex-end",
                                    alignItems: "center"
                                }}
                            >
                                <Grid
                                    xs="auto"
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "3px"
                                    }}
                                >
                                    <Grid item>
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                whiteSpace: "nowrap",
                                                textAlign: "right",
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            Total Investment
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="body_600"
                                            sx={(theme: Theme) => ({
                                                whiteSpace: "nowrap",
                                                textAlign: "right",
                                                lineHeight: "25px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            {formatCurrency({ value: familyData?.totalInvestment, locale, currencyCode })}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid
                                    xs="auto"
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "3px"
                                    }}
                                >
                                    <Grid item>
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                whiteSpace: "nowrap",
                                                textAlign: "right",
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            Returns
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="body_600"
                                            sx={(theme: Theme) => ({
                                                whiteSpace: "nowrap",
                                                textAlign: "right",
                                                lineHeight: "25px",
                                                color: familyData?.returns >= 0 ? theme.palette.success[500] : theme.palette.error[500]
                                            })}
                                        >
                                            {familyData?.returns > 0 ? '+' : ''} {formatCurrency({ value: familyData?.returns, locale, currencyCode })}
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid
                                    xs="auto"
                                    item
                                    container
                                    sx={{
                                        flexDirection: "column",
                                        rowGap: "3px"
                                    }}
                                >
                                    <Grid item>
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                whiteSpace: "nowrap",
                                                textAlign: "right",
                                                lineHeight: "20px",
                                                letterSpacing: "0.02px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            Absolute Return
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="body_600"
                                            sx={(theme: Theme) => ({
                                                whiteSpace: "nowrap",
                                                textAlign: "right",
                                                lineHeight: "25px",
                                                color: familyData?.irr >= 0 ? theme.palette.success[500] : theme.palette.error[500]
                                            })}
                                        >
                                            {familyData?.irr?.toFixed(1)}%
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )
            }
        </Grid>
    )
};

export default function FamilyDetails(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch()

    const { pathname: pathName, state } = history.location;
    const navState = state as navigationData

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id)
    const { businessDate } = useSelector((state: RootStateType) => state.navbarInfo)
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);

    const [familyData, setFamilyData] = useState<FamilyDetailsData>({} as FamilyDetailsData)
    const [isFamilyDataLoading, setFamilyDataLoading] = useState<boolean>(false)
    const [open, setOpen] = useState(false);
    const [apiError, setApiError] = useState<boolean>(true);
    const [isSuccess, setIsSuccess] = useState<boolean>(true);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setFamilyDataLoading(true)

                const response = await (dispatch(getFamilyDetails({
                    familyName: navState?.familyName ?? "",
                    rmId: `${iwappRmId}`
                })) as unknown as Promise<GetFamilyDetailsResponseBody>)

                if (response?.isSuccess) {
                    const modifiedRes: FamilyDetailsData = {
                        ...response.result,
                        familyName: maskInfo(response.result?.familyName),
                        familyMembers: response.result?.familyMembers?.map((item: any) => ({
                            ...item,
                            name: maskInfo(item.name)
                        })),
                        familyDistribution: {
                            ...response.result?.familyDistribution,
                            labelData: response.result?.familyDistribution?.labelData?.map((item: any) => ({
                                ...item,
                                label: maskInfo(item.label)
                            }))
                        }

                    }

                    // console.log('modifiedRes', modifiedRes)
                    setFamilyData(modifiedRes)
                    setApiError(false)
                    setIsSuccess(true)
                } else {
                    setApiError(true)
                    setIsSuccess(false)
                }


            } catch (error) {
                setApiError(true)
                setIsSuccess(false)
                console.log("Error: ", error)
            } finally {
                setFamilyDataLoading(false)
            }
        }


        fetchData()
    }, [])


    const initialRouter = pathName.split('/').filter((item) => item !== '')

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            state: {
                accountFilter: navState?.accountFilter,
            }
        },
        {
            name: `${initialRouter[1]}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode
            }
        },
    ] : [])

    const handleClickEvent = (clientCode: string, investorName: string, portfolio: string, accountType: string) => {
        dispatch(storeInvestorClientId({ investorClientId: clientCode || "" }));
        dispatch(storeInterfaceCode(portfolio || ""));
        dispatch(storeSelectedPortfolio({ label: investorName, value: portfolio }))
        history.push(`/investor-profile/${investorName}`, {
            clientCode: clientCode,
            portfoiloId: portfolio,
            accountType: accountType ?? 'EXY',
        })
    }

    return (
        <CommonLayout>
            <>
                <BreadCrum array={routerPath} />
                <Hidden lgDown>
                    <Grid
                        container
                        mt={3}
                        spacing={{ xs: 4 }}
                    >
                        <Grid
                            item
                            xs={12}
                            md={8}
                            gap={4}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >

                            <Grid
                                item
                                xs={12}
                                sx={(theme: Theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                    padding: "35px",
                                    borderRadius: "25px",
                                    flexWrap: "nowrap",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    height: "fit-content"
                                })}
                            >
                                <FamilyDetailsInfo isLoading={isFamilyDataLoading} familyData={familyData} apiError={apiError} isSuccess={isSuccess} />
                            </Grid>
                            <Grid item xs={12}>
                                <FamilyCards isLoading={isFamilyDataLoading} familyData={familyData} handleClickEvent={handleClickEvent} apiError={apiError} isSuccess={isSuccess} />
                            </Grid>
                        </Grid>
                        <Grid item xs={4}>
                            <FamilyChart isLoading={isFamilyDataLoading} familyData={familyData} open={open} setOpen={setOpen} apiError={apiError} />
                        </Grid>
                    </Grid>
                </Hidden>
                <Hidden lgUp>
                    <Grid
                        container
                        sx={{
                            marginTop: "25px",
                            flexDirection: "column",
                            rowGap: "25px"
                        }}
                    >
                        <Grid
                            xs={12}
                            item
                            container
                            sx={{
                                flexDirection: "column",
                                rowGap: "25px",

                            }}
                        >
                            <Grid
                                item
                                container
                                sx={(theme: Theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.white}0D`,
                                    padding: "35px",
                                    borderRadius: "25px",
                                    flexWrap: "nowrap",
                                    justifyContent: "space-between",

                                })}
                            >
                                <FamilyDetailsInfo isLoading={isFamilyDataLoading} familyData={familyData} apiError={apiError} isSuccess={isSuccess} />
                            </Grid>
                        </Grid>
                        <Grid
                            xs={12}
                            item
                        >
                            <FamilyChart isLoading={isFamilyDataLoading} familyData={familyData} open={open} setOpen={setOpen} apiError={apiError} />
                        </Grid>
                        <Grid item xs={12}>
                            <FamilyCards isLoading={isFamilyDataLoading} familyData={familyData} handleClickEvent={handleClickEvent} apiError={apiError} isSuccess={isSuccess} />
                        </Grid>
                    </Grid>
                </Hidden>
            </>
        </CommonLayout >
    )
}
